// ============================================================================
// modaliteVote.js — LA source unique des modalités de vote d'un point
// ============================================================================
//
// Trois modalités, et aucune autre (demande Pierre du 22/09/2026 : « il manque
// une modalité de vote : vote à bulletin secret en séance ») :
//
//   • electronique — chacun vote depuis son appareil avec son code ; le
//                    scrutin peut être public ou secret ;
//   • main_levee   — en séance, l'équipe saisit le résultat constaté dans la
//                    salle (unanimité en deux gestes, ou qui a voté quoi) ;
//   • bulletin     — en séance, À BULLETIN SECRET : l'équipe saisit le
//                    DÉPOUILLEMENT (pour, contre, abstentions, blancs, nuls —
//                    ou les voix de chaque candidat). Aucun vote nominatif :
//                    personne, pas même l'équipe, ne sait qui a voté quoi.
//
// STOCKAGE, inchangé et compatible avec l'existant : `modeVote` vaut
// "electronique" ou "seance" ; EN SÉANCE, le caractère secret (typeVote sur
// le point d'ODJ, `secret` sur le scrutin) distingue la main levée du bulletin
// secret. Conséquence voulue : toutes les gardes `modeVote === "seance"`
// existantes (le vote électronique est fermé sur l'appareil des délégués,
// Participant.js) couvrent le bulletin secret sans une ligne de plus.
//
// Avant le 22/09, choisir « en séance » FORÇAIT le vote public et la
// synchronisation de l'ODJ neutralisait le secret : un point réglé
// « secret + en séance » (cas de l'élection d'un administrateur, que les
// statuts veulent à bulletins secrets) devenait en silence un vote public à
// main levée. Aucun scrutin passé ne portait cette combinaison.
// ============================================================================

export const MODALITES = {
  electronique: { cle: "electronique", icone: "📱", court: "Électronique (codes)", libelle: "Vote électronique" },
  main_levee: { cle: "main_levee", icone: "✋", court: "En séance (main levée)", libelle: "Vote à main levée" },
  bulletin: { cle: "bulletin", icone: "🗳", court: "En séance (bulletin secret)", libelle: "Vote à bulletin secret en séance" },
};

/** Point d'ODJ (typeVote) OU scrutin de séance (secret booléen). */
export function estSecret(pt) {
  if (!pt) return false;
  return typeof pt.secret === "boolean" ? pt.secret : pt.typeVote === "secret";
}

/** "electronique" | "main_levee" | "bulletin" */
export function modaliteVote(pt) {
  if (!pt || pt.modeVote !== "seance") return "electronique";
  return estSecret(pt) ? "bulletin" : "main_levee";
}

const typeDe = (pt) => (pt && (pt.type || pt.scrutinType)) || "yon";

/**
 * Le résultat se saisit-il par DÉPOUILLEMENT (des nombres, aucun nom) plutôt
 * que voix par voix ? Oui pour le bulletin secret, et pour une ÉLECTION en
 * séance quelle qu'elle soit : le panneau à main levée ne sait dire que
 * pour / contre, ce qui n'a pas de sens face à des candidats.
 */
export function saisieParDepouillement(s) {
  if (!s || s.modeVote !== "seance") return false;
  return modaliteVote(s) === "bulletin" || typeDe(s) === "nominatif";
}

/** Les modalités proposées pour un type de scrutin (vide = pas de choix). */
export function modalitesProposees(scrutinType = "yon") {
  const t = scrutinType || "yon";
  if (t === "yon") return [MODALITES.electronique, MODALITES.main_levee, MODALITES.bulletin];
  // Une élection en séance se tient à bulletin secret (statuts d'Enio :
  // « scrutin uninominal à un tour, à bulletins secrets »).
  if (t === "nominatif") return [MODALITES.electronique, MODALITES.bulletin];
  return [];
}

/** Les champs à poser sur le point d'ODJ quand on choisit une modalité. */
export function champsModalite(cle) {
  if (cle === "main_levee") return { modeVote: "seance", typeVote: "public", scrutin: true };
  if (cle === "bulletin") return { modeVote: "seance", typeVote: "secret", scrutin: true };
  return { modeVote: "electronique" };
}

/**
 * L'expression de la modalité, telle qu'on l'écrit dans le déroulé, l'ordre
 * du jour et les exports :
 *   « Vote électronique · scrutin public » / « Vote électronique · scrutin secret »
 *   « Vote à main levée »
 *   « Vote à bulletin secret en séance »
 */
export function libelleModalite(pt) {
  const m = modaliteVote(pt);
  if (m === "electronique") return `${MODALITES.electronique.libelle} · scrutin ${estSecret(pt) ? "secret" : "public"}`;
  return MODALITES[m].libelle;
}

/** Pictogramme compact (ligne repliée d'un point, liste des scrutins). */
export function iconeModalite(pt) {
  const m = modaliteVote(pt);
  if (m === "electronique") return estSecret(pt) ? "🔒" : "📢";
  return MODALITES[m].icone;
}

// ── Bulletins dépouillés ────────────────────────────────────────────────────
// Un bulletin blanc ou nul n'est pas un suffrage exprimé : il compte parmi les
// votants (la participation), jamais dans une majorité ni pour un candidat.
export const CHOIX_BLANC = "blanc";
export const CHOIX_NUL = "nul";

export function estBlancOuNul(v) {
  return !!v && v.bulletin === true && (v.choice === CHOIX_BLANC || v.choice === CHOIX_NUL);
}

/**
 * Les voix d'une élection, blancs et nuls à part. Remplace les six comptages
 * « par candidat » qui existaient (écran de salle, résultats, exports, PV) :
 * sans lui, un bulletin blanc serait affiché comme un candidat nommé « blanc ».
 * Les expressions libres de l'ancienne saisie manuelle restent hors comptage.
 */
export function compterCandidats(votes = []) {
  const par = {};
  let blancs = 0, nuls = 0;
  for (const v of votes || []) {
    if (!v || v.expression) continue;
    if (v.bulletin === true && v.choice === CHOIX_BLANC) { blancs++; continue; }
    if (v.bulletin === true && v.choice === CHOIX_NUL) { nuls++; continue; }
    if (!v.choice) continue;
    par[v.choice] = (par[v.choice] || 0) + 1;
  }
  const candidats = Object.entries(par).sort((a, b) => b[1] - a[1]);
  return { candidats, blancs, nuls };
}
